import { useState, useEffect } from 'preact/hooks';
import { Chapter, Comment, User } from '@novelizing/types';

// Get API_BASE from environment variable REACTION_API_URL
const getApiBase = (): string => {
  const apiBase =
    import.meta.env.VITE_REACTION_API_URL || 'https://reactions.novelizing.com';

  if (!apiBase) {
    throw new Error('VITE_REACTION_API_URL environment variable is not set');
  }
  return apiBase;
};

interface UseCommentsProps {
  chapter: Chapter;
}

export const useComments = ({ chapter }: UseCommentsProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch comments for the entity
  const fetchComments = async () => {
    if (!chapter.id) return;

    setLoading(true);
    setError(null);

    try {
      // Get API_BASE from environment variable
      const apiBase = getApiBase();
      const response = await fetch(
        `${apiBase}/comments/for/chapter/${chapter.id}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch comments: ${response.status}`);
      }

      const data = await response.json();
      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Add a new comment
  const addComment = async (
    content: string,
    user: User,
    parentId?: string,
  ) => {
    if (!chapter.id || !content) return;

    setLoading(true);
    setError(null);

    try {
      // Get API_BASE from environment variable
      const apiBase = getApiBase();
      const entityType = 'chapter';

      // Prepare the request body
      const requestBody: any = {
        content,
        userId: user.id,
        entityId: chapter.id,
        entityType,
      };

      // Add parentId to the request body if it exists (for replies)
      if (parentId) {
        requestBody.parentId = parentId;
      }

      const response = await fetch(`${apiBase}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to add comment: ${response.status}`);
      }

      // Refresh comments after adding a new one
      fetchComments();
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Fetch comments on mount and when entityId changes
  useEffect(() => {
    if (chapter.id) {
      fetchComments();
    }
  }, [chapter.id]);

  // Delete a comment
  const deleteComment = async (commentId: string) => {
    if (!commentId) return;

    setLoading(true);
    setError(null);

    try {
      // Get API_BASE from environment variable
      const apiBase = getApiBase();

      const response = await fetch(`${apiBase}/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete comment: ${response.status}`);
      }

      // Refresh comments after deleting one
      fetchComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };
  
  // Update a comment
  const updateComment = async (commentId: string, content: string) => {
    if (!commentId || !content) return;

    setLoading(true);
    setError(null);

    try {
      // Get API_BASE from environment variable
      const apiBase = getApiBase();

      const response = await fetch(`${apiBase}/comments/${commentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to update comment: ${response.status}`);
      }

      // Refresh comments after updating one
      fetchComments();
    } catch (err) {
      console.error('Error updating comment:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return {
    comments,
    loading,
    error,
    addComment,
    deleteComment,
    updateComment,
    refreshComments: fetchComments,
  };
};
